import './introAssets/introStyles.css'
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Howl } from 'howler';
import ChantAudioControl from './introAssets/ChantAudioControl.jsx';
import TelosPrelude from './introAssets/text/TelosPrelude.jsx';

export default function Intro() {
  const [hasStartedScroll, setHasStartedScroll] = useState(false);
  const [loadNextStage, setLoadNextStage] = useState(false);
  const [autoScrollOn, setAutoScrollOn] = useState(false);

  const navigate = useNavigate();
  const rafRef = useRef(null);

  // Preload Zen Chime sound
  const zenChimeSound = new Howl({
    src: ['./audio/Zen-Bell-Single-Chime.mp3'],
    volume: 1,
    loop: false,
    preload: true,
    onload: () => console.log('Zen chime preloaded successfully'),
  });

  // Mark that the user started scrolling (also used for ChantAudioControl)
  useEffect(() => {
    const detectScrolling = () => {
      if (!hasStartedScroll) setHasStartedScroll(true);
    };

    window.addEventListener('scroll', detectScrolling, { passive: true });

    return () => {
      window.removeEventListener('scroll', detectScrolling);
    };
  }, [hasStartedScroll]);

  const playZenChime = useCallback(() => {
    console.log('Playing Zen Chime...');
    zenChimeSound.play();
    zenChimeSound.fade(zenChimeSound.volume(), 0, 5000);
    zenChimeSound.once('fade', () => {
      zenChimeSound.stop();
    });
  }, [zenChimeSound]);

  const handleRedButtonClick = useCallback(() => {
    document.body.classList.add('page-transition');
    setLoadNextStage(true);
    playZenChime();
    navigate('/Stage0');
  }, [playZenChime, navigate]);

  const handleBlueButtonClick = useCallback(() => {
    document.body.classList.add('page-transition');
    setLoadNextStage(true);
    playZenChime();
    navigate('/analyzeMap')
  }, [playZenChime]);

  // -------- Auto scroll engine ----------
  useEffect(() => {
    // always cancel any existing loop first
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    if (!autoScrollOn) return;

    // Consider auto-scroll as "started"
    if (!hasStartedScroll) setHasStartedScroll(true);

    // Tune these:
    const SPEED_PX_PER_FRAME = 5.0; // lower = slower, higher = faster
    const BOTTOM_BUFFER_PX = 6;     // stop near bottom

    const step = () => {
      const doc = document.documentElement;
      const maxScrollTop = doc.scrollHeight - window.innerHeight;

      // If we're basically at the bottom, stop.
      if (window.scrollY >= maxScrollTop - BOTTOM_BUFFER_PX) {
        setAutoScrollOn(false);
        return;
      }

      window.scrollBy(0, SPEED_PX_PER_FRAME);
      rafRef.current = requestAnimationFrame(step);
    };

    rafRef.current = requestAnimationFrame(step);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [autoScrollOn, hasStartedScroll]);

// Stop auto-scroll ONLY if the user manually scrolls (wheel / finger drag).
useEffect(() => {
  if (!autoScrollOn) return;

  const stop = () => setAutoScrollOn(false);

  // Desktop / trackpad manual scroll
  const onWheel = () => stop();

  // Mobile manual scroll: detect a touch-drag that actually changes scrollY
  let startY = 0;
  let startScrollY = 0;

  const onTouchStart = () => {
    startY = window.scrollY;
    startScrollY = window.scrollY;
  };

  const onTouchMove = () => {
    // If the user drag-scrolls, scrollY will change from where it was at touchstart
    if (window.scrollY !== startScrollY) stop();
  };

  window.addEventListener("wheel", onWheel, { passive: true });
  window.addEventListener("touchstart", onTouchStart, { passive: true });
  window.addEventListener("touchmove", onTouchMove, { passive: true });

  return () => {
    window.removeEventListener("wheel", onWheel);
    window.removeEventListener("touchstart", onTouchStart);
    window.removeEventListener("touchmove", onTouchMove);
  };
}, [autoScrollOn]);

  const toggleAutoScroll = useCallback(() => {
    setAutoScrollOn((prev) => !prev);
    // small nudge so iOS/Safari feels responsive
    window.scrollBy(0, 1);
  }, []);

  return (
    <>
      <div className="chant-control">
        <ChantAudioControl
          shouldPlay={hasStartedScroll}
          onScrollStart={() => setHasStartedScroll(true)}
          />
      </div>

      <header className="phrase-container fullHeight">
        <h1 className="main-title">TELOS</h1>
        <h3 className="main-title">A Story of Identity</h3>
        {/* <h5> Turn Phone Sideways </h5> */}

        {/* ✅ Scroll + Auto scroll controls */}
        <div className="scroll-controls">
          {/* <span className="scroll-instructions">(Scroll for More)</span> */}

          <button
            type="button"
            className={`auto-scroll-link ${autoScrollOn ? 'is-on' : ''}`}
            onClick={toggleAutoScroll}
            aria-pressed={autoScrollOn}
            >
            {autoScrollOn ? '(Auto Scroll: On)' : '(Click to Auto Scroll)'}
          </button>
        </div>
      </header>

      <TelosPrelude />
      <div className="short-pause"></div>

      <div className="background">
        <div className="end-container">

          <h1>Select a Way of Knowing:</h1>

          <div className="button-wrapper">
            <button onClick={handleRedButtonClick} className="red button">
              Have An Experience
            </button>
            <button onClick={handleBlueButtonClick} className="blue button">
              Analyze the Maps
            </button>
          </div>

        </div>
      </div>
    </>
  );
}
